import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Building2, Mail, Phone, FileText, ChevronDown, ChevronRight, RefreshCw, Shield, BellRing, X, Check } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, Thead, Tbody, Tr, Th, Td } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { useClient, useClientContracts, useClientContacts, useClientNotificationRecipients, useUpdateClientNotificationRecipients } from '../../hooks/use-clients';
import { useUsers } from '../../hooks/use-users';
import { useToast } from '../../components/ui/toast';
import type { ClientContract, User } from '../../types';

function formatDate(d: string | null | undefined) {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleDateString('ro-RO');
}

function ContractRow({ contract }: { contract: ClientContract }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <Tr className="cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <Td className="font-medium">
          <span className="inline-flex items-center gap-1">
            {contract.subcategories.length > 0 && (
              expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
            )}
            {contract.name}
          </span>
        </Td>
        <Td className="text-sm text-[color:var(--ink-muted)]">{contract.supportContractNumber || '—'}</Td>
        <Td className="text-sm text-[color:var(--ink-muted)]">{contract.supportType || '—'}</Td>
        <Td className="text-sm text-[color:var(--ink-muted)]">{formatDate(contract.startDate)}</Td>
        <Td className="text-sm text-[color:var(--ink-muted)]">{formatDate(contract.endDateReal || contract.endDateInitial)}</Td>
        <Td>
          <div className="flex gap-1">
            {contract.active && <Badge className="bg-green-100 text-green-700">Activ</Badge>}
            {contract.isSupport && <Badge className="bg-blue-100 text-blue-700">Suport</Badge>}
          </div>
        </Td>
      </Tr>
      {expanded && contract.subcategories.length > 0 && (
        <>
          {contract.subcategories.map((sub) => (
            <Tr key={sub.elementId} className="bg-[color:var(--surface-panel-strong)]/80 hover:bg-[color:var(--surface-panel-strong)]">
              <Td className="pl-10 text-sm text-[color:var(--ink-base)]" colSpan={4}>↳ {sub.name}</Td>
              <Td />
              <Td>
                {sub.active && <Badge className="bg-green-100 text-green-700 text-xs">Activ</Badge>}
              </Td>
            </Tr>
          ))}
        </>
      )}
    </>
  );
}

function NotificationRecipientsSection({ clientId }: { clientId: string }) {
  const { toast } = useToast();
  const { data: recipientsData, isLoading: recipientsLoading } = useClientNotificationRecipients(clientId);
  const { data: agentsData, isLoading: agentsLoading } = useUsers({ pageSize: 100, role: 'AGENT' });
  const { data: adminsData, isLoading: adminsLoading } = useUsers({ pageSize: 100, role: 'ADMIN' });
  const updateRecipients = useUpdateClientNotificationRecipients(clientId);
  const usersLoading = agentsLoading || adminsLoading;

  const savedRecipientIds = recipientsData?.data ?? [];
  const [selectedIds, setSelectedIds] = useState<string[] | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Merge agents + admins into a single list
  const allStaff = useMemo(() => {
    const agents = agentsData?.data ?? [];
    const admins = adminsData?.data ?? [];
    const map = new Map<string, User>();
    for (const u of [...agents, ...admins]) {
      if (u.isActive) map.set(u.id, u);
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [agentsData, adminsData]);

  // Current working list (local state takes precedence once user starts editing)
  const currentIds = selectedIds ?? savedRecipientIds;
  const hasChanges = selectedIds !== null && (
    selectedIds.length !== savedRecipientIds.length ||
    selectedIds.some((id) => !savedRecipientIds.includes(id))
  );

  const filteredStaff = useMemo(() => {
    if (!searchTerm) return allStaff;
    const q = searchTerm.toLowerCase();
    return allStaff.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  }, [allStaff, searchTerm]);

  const toggleUser = (userId: string) => {
    const current = selectedIds ?? [...savedRecipientIds];
    if (current.includes(userId)) {
      setSelectedIds(current.filter((id) => id !== userId));
    } else {
      setSelectedIds([...current, userId]);
    }
  };

  const handleSave = () => {
    if (!selectedIds) return;
    updateRecipients.mutate(selectedIds, {
      onSuccess: () => {
        setSelectedIds(null);
        toast('success', 'Destinatarii de notificare au fost salvați cu succes');
      },
      onError: (e) => toast('error', `Eroare la salvare: ${e.message}`),
    });
  };

  const handleReset = () => setSelectedIds(null);

  if (recipientsLoading || usersLoading) return <Skeleton className="h-40 w-full" />;

  const selectedUsers = allStaff.filter((u) => currentIds.includes(u.id));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-semibold"><BellRing size={18} /> Notificări tichete noi</h2>
          {hasChanges && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleReset} disabled={updateRecipients.isPending}>
                Anulează
              </Button>
              <Button size="sm" onClick={handleSave} disabled={updateRecipients.isPending}>
                <Check size={14} className="mr-1" />
                {updateRecipients.isPending ? 'Se salvează...' : 'Salvează'}
              </Button>
            </div>
          )}
        </div>
        <p className="text-sm text-[color:var(--ink-muted)]">
          Selectează utilizatorii care vor fi notificați la intrarea unui tichet nou de la acest client.
          Dacă nu este selectat nimeni, toți agenții și adminii vor fi notificați.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Selected users chips */}
        {selectedUsers.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedUsers.map((u) => (
              <span
                key={u.id}
                className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary"
              >
                {u.name}
                <button
                  type="button"
                  onClick={() => toggleUser(u.id)}
                  className="ml-1 rounded-full p-0.5 hover:bg-primary/20"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
        {selectedUsers.length === 0 && (
          <p className="text-sm italic text-amber-600 dark:text-amber-400">
            Niciun utilizator selectat — toți agenții și adminii vor fi notificați.
          </p>
        )}

        {/* Search + user list */}
        <input
          type="text"
          placeholder="Caută utilizator..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full rounded-lg border bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/50 dark:border-dark-border"
        />
        <div className="max-h-60 overflow-y-auto rounded-lg border dark:border-dark-border">
          {filteredStaff.map((user) => {
            const isSelected = currentIds.includes(user.id);
            return (
              <button
                key={user.id}
                type="button"
                onClick={() => toggleUser(user.id)}
                className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-[color:var(--surface-panel-strong)] ${
                  isSelected ? 'bg-primary/5' : ''
                }`}
              >
                <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                  isSelected ? 'border-primary bg-primary text-white' : 'border-gray-300 dark:border-dark-border'
                }`}>
                  {isSelected && <Check size={12} />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{user.name}</p>
                  <p className="text-xs text-[color:var(--ink-muted)] truncate">{user.email}</p>
                </div>
                <Badge className={`text-xs ${user.role === 'ADMIN' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                  {user.role === 'ADMIN' ? 'Admin' : 'Agent'}
                </Badge>
              </button>
            );
          })}
          {filteredStaff.length === 0 && (
            <p className="p-3 text-center text-sm text-gray-400">Niciun utilizator găsit</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { data: clientData, isLoading } = useClient(id!);
  const { data: contractsData } = useClientContracts(id!);
  const { data: contactsData } = useClientContacts(id!);
  const client = clientData?.data;
  const contracts = contractsData?.data ?? [];
  const contacts = contactsData?.data ?? [];
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Invalidate backend cache
      await fetch('/api/settings/webcon/refresh', {
        method: 'POST',
        credentials: 'include',
      });
    } catch { /* ignore */ }
    // Invalidate TanStack Query caches for this client
    await queryClient.invalidateQueries({ queryKey: ['clients', id] });
    await queryClient.invalidateQueries({ queryKey: ['clients', id, 'contacts'] });
    await queryClient.invalidateQueries({ queryKey: ['clients', id, 'contracts'] });
    setRefreshing(false);
  };

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-40 w-full" /></div>;
  if (!client) return <p className="text-center text-gray-500">Clientul nu a fost găsit</p>;

  return (
    <div className="space-y-6">
      <Link to="/admin/clients" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-dark-muted dark:hover:text-dark-text">
        <ArrowLeft size={16} /> Înapoi la clienți
      </Link>

      <div className="flex items-center gap-3">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10">
          <Building2 className="text-primary" size={28} />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">{client.name}</h1>
          {client.clientCode && <p className="text-sm text-gray-500 dark:text-dark-muted">Cod: {client.clientCode}</p>}
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Se actualizează...' : 'Reîmprospătează'}
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {(client.companyEmail || client.contactEmail) && (
          <Card>
            <CardContent className="flex items-center gap-3 text-sm">
              <Mail size={16} className="text-gray-400" />
              <span>{client.companyEmail || client.contactEmail}</span>
            </CardContent>
          </Card>
        )}
        {client.phone && (
          <Card>
            <CardContent className="flex items-center gap-3 text-sm">
              <Phone size={16} className="text-gray-400" />
              <span>{client.phone}</span>
            </CardContent>
          </Card>
        )}
      </div>

      {/* SLA from WebCon */}
      {client.sla && (
        <Card>
          <CardHeader>
            <h2 className="flex items-center gap-2 font-semibold"><Shield size={18} /> SLA</h2>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
              {[
                { label: 'Răspuns Critic', value: client.sla.criticalResponseTime, unit: 'h' },
                { label: 'Rezolvare Critic', value: client.sla.criticalSolvingTime, unit: 'h' },
                { label: 'Răspuns Normal', value: client.sla.normalResponseTime, unit: 'h' },
                { label: 'Rezolvare Normal', value: client.sla.normalSolvingTime, unit: 'h' },
                { label: 'Răspuns Estetic', value: client.sla.esteticResponseTime, unit: 'h' },
                { label: 'Rezolvare Estetic', value: client.sla.esteticSolvingTime, unit: 'h' },
              ].map((item) => (
                <div key={item.label} className="rounded-lg border p-3 dark:border-dark-border">
                  <p className="text-xs text-gray-500 dark:text-dark-muted">{item.label}</p>
                  <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-dark-text">{item.value ? `${item.value}${item.unit}` : '—'}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notification Recipients */}
      <NotificationRecipientsSection clientId={id!} />

      {/* Contracts */}
      <Card>
        <CardHeader>
          <h2 className="flex items-center gap-2 font-semibold"><FileText size={18} /> Contracte/Proiecte ({contracts.length})</h2>
        </CardHeader>
        <Table>
          <Thead>
            <Tr>
              <Th>Nume</Th>
              <Th>Nr. contract</Th>
              <Th>Tip suport</Th>
              <Th>Start</Th>
              <Th>Sfârșit</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {contracts.length === 0 && (
              <Tr><Td colSpan={6} className="text-center text-gray-400">Niciun contract</Td></Tr>
            )}
            {contracts.map((c) => (
              <ContractRow key={c.elementId} contract={c} />
            ))}
          </Tbody>
        </Table>
      </Card>

      {/* Contacts */}
      <Card>
        <CardHeader>
          <h2 className="flex items-center gap-2 font-semibold"><Mail size={18} /> Persoane de contact ({contacts.length})</h2>
        </CardHeader>
        <Table>
          <Thead>
            <Tr>
              <Th>Nume</Th>
              <Th>Email</Th>
              <Th>Telefon</Th>
              <Th>Funcție</Th>
            </Tr>
          </Thead>
          <Tbody>
            {contacts.length === 0 && (
              <Tr><Td colSpan={4} className="text-center text-gray-400">Nicio persoană de contact</Td></Tr>
            )}
            {contacts.map((cp: any, i: number) => (
              <Tr key={i}>
                <Td className="font-medium">{cp.name}</Td>
                <Td className="text-sm text-gray-500">{cp.email}</Td>
                <Td className="text-sm text-gray-500">{cp.phone ?? '—'}</Td>
                <Td className="text-sm text-gray-500">{cp.position ?? '—'}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Card>
    </div>
  );
}
